if (!customElements.get('homepage-ending-video')) {
  customElements.define('homepage-ending-video', class extends HTMLElement {
    static apiPromise;

    static loadYouTube() {
      if (window.YT?.Player) return Promise.resolve(window.YT);
      if (this.apiPromise) return this.apiPromise;
      this.apiPromise = new Promise((resolve, reject) => {
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
          const script = document.createElement('script');
          script.src = 'https://www.youtube.com/iframe_api';
          script.async = true;
          document.head.appendChild(script);
        }
        const deadline = Date.now() + 10000;
        const timer = setInterval(() => {
          if (window.YT?.Player) {
            clearInterval(timer);
            resolve(window.YT);
          } else if (Date.now() >= deadline) {
            clearInterval(timer);
            this.apiPromise = null;
            reject(new Error('YouTube API unavailable'));
          }
        }, 100);
      });
      return this.apiPromise;
    }

    connectedCallback() {
      this.controller = new AbortController();
      this.loading = null;
      const options = { signal: this.controller.signal };
      this.media = this.querySelector('[data-ending-media]');
      this.native = this.media?.querySelector('video');
      this.button = this.querySelector('[data-ending-toggle]');
      this.openButton = this.querySelector('[data-ending-open]');
      this.dialog = this.querySelector('dialog');
      this.status = this.querySelector('[data-ending-status]');
      if (!this.media || !this.button || !this.dialog) return;
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.visible = this.playing = this.playPending = this.userPaused = this.manualPlay = false;
      this.button.hidden = this.openButton.hidden = false;
      this.button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.userPaused = this.playing || this.playPending;
        this.manualPlay = !this.userPaused;
        this.status.textContent = '';
        this.sync();
      }, options);
      this.openButton.addEventListener('click', () => this.openDialog(), options);
      this.querySelector('[data-ending-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        this.querySelector('[data-ending-dialog-player]').replaceChildren();
        document.documentElement.style.overflow = this.previousOverflow;
        this.openButton.focus({ preventScroll: true });
        this.sync();
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      }, options);
      if (this.native) {
        this.native.muted = true;
        this.native.addEventListener('play', () => this.onPlayback(true), options);
        this.native.addEventListener('pause', () => this.onPlayback(false), options);
        this.native.addEventListener('error', () => this.showError(), options);
      }
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.reduced.addEventListener('change', () => {
        this.manualPlay = false;
        this.sync();
      }, options);
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        if (!this.visible) this.manualPlay = false;
        this.sync();
      }, { threshold: 0.1 });
      this.observer.observe(this.querySelector('.ending-video__frame'));
      this.updateButton();
    }

    shouldPlay() {
      return this.isConnected && this.visible && !document.hidden && (this.native || !this.dialog.open) && !this.userPaused && (!this.reduced.matches || this.manualPlay);
    }

    async prepareYouTube() {
      if (this.ready) return;
      if (this.loading) return this.loading;
      const signal = this.controller.signal;
      let active = true;
      this.loading = (async () => {
        const YT = await this.constructor.loadYouTube();
        if (signal.aborted) return;
        const iframe = this.media.querySelector('template').content.firstElementChild.cloneNode(true);
        const url = new URL(iframe.src);
        url.searchParams.set('origin', location.origin);
        iframe.src = url.href;
        this.media.appendChild(iframe);
        await new Promise((resolve, reject) => {
          this.readyTimer = setTimeout(() => reject(new Error('YouTube player unavailable')), 10000);
          this.yt = new YT.Player(iframe, { events: {
            onReady: (event) => {
              clearTimeout(this.readyTimer);
              if (active && !signal.aborted) {
                event.target.mute();
                this.ready = true;
              }
              resolve();
            },
            onStateChange: (event) => {
              if (active && !signal.aborted && [0, 1, 2].includes(event.data)) this.onPlayback(event.data === 1);
            },
            onError: () => {
              if (!active || signal.aborted) return;
              clearTimeout(this.readyTimer);
              reject(new Error('YouTube rejected this video'));
              if (!signal.aborted) this.showError();
            },
            onAutoplayBlocked: () => {
              if (active && !signal.aborted) {
                this.status.textContent = 'Autoplay was blocked. Select Play video to try again.';
                this.onPlayback(false);
              }
            }
          }});
          signal.addEventListener('abort', () => {
            active = false;
            clearTimeout(this.readyTimer);
            resolve();
          }, { once: true });
        });
      })().catch(() => {
        active = false;
        if (signal.aborted) return;
        this.yt?.destroy();
        this.yt = null;
        this.ready = false;
        this.media.querySelector('iframe')?.remove();
        if (!signal.aborted) this.showError();
      }).finally(() => {
        if (this.controller.signal === signal) this.loading = null;
      });
      return this.loading;
    }

    async sync() {
      if (!this.shouldPlay()) {
        this.pause();
        return;
      }
      this.playPending = true;
      this.updateButton();
      if (this.native) {
        this.native.muted = true;
        this.native.play().catch((error) => {
          if (error.name !== 'AbortError' && this.shouldPlay()) this.showError();
        });
      } else {
        await this.prepareYouTube();
        if (this.ready && this.shouldPlay()) {
          this.yt.mute();
          this.yt.playVideo();
        } else this.pause();
      }
    }

    pause() {
      this.playPending = false;
      this.updateButton();
      this.native?.pause();
      if (this.ready) this.yt.pauseVideo();
    }

    onPlayback(playing) {
      this.playPending = false;
      this.playing = playing;
      if (playing) {
        this.setAttribute('data-started', '');
        this.status.textContent = '';
        if (!this.shouldPlay()) this.pause();
      }
      this.updateButton();
    }

    updateButton() {
      this.button.textContent = this.playing || this.playPending ? this.button.dataset.pauseLabel : this.button.dataset.playLabel;
      this.button.toggleAttribute('data-playing', this.playing || this.playPending);
      this.button.setAttribute('aria-label', this.button.textContent);
      this.button.title = this.button.textContent;
    }

    showError() {
      this.status.textContent = 'Video could not load. Check your connection and select Play video to retry, or open Watch full video.';
      this.playing = false;
      this.playPending = false;
      this.updateButton();
    }

    openDialog() {
      this.previousOverflow = document.documentElement.style.overflow;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      if (!this.native) this.pause();
      let player;
      if (this.native && !this.media.querySelector('template')) {
        player = this.native.cloneNode(true);
        player.muted = false;
        player.removeAttribute('muted');
        player.loop = false;
        player.controls = true;
        player.autoplay = true;
      } else {
        player = this.media.querySelector('template').content.firstElementChild.cloneNode(true);
        const url = new URL(player.src);
        for (const [key, value] of Object.entries({ autoplay: '1', controls: '1', mute: '0', loop: '0', origin: location.origin })) url.searchParams.set(key, value);
        url.searchParams.delete('playlist');
        player.src = url.href;
      }
      this.querySelector('[data-ending-dialog-player]').replaceChildren(player);
      this.querySelector('[data-ending-close]').focus();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      clearTimeout(this.readyTimer);
      this.native?.pause();
      this.yt?.destroy();
      this.yt = null;
      this.ready = false;
      this.media?.querySelector('iframe')?.remove();
      if (this.dialog?.open) {
        this.dialog.close();
        document.documentElement.style.overflow = this.previousOverflow;
        this.querySelector('[data-ending-dialog-player]').replaceChildren();
      }
    }
  });
}
